import { Component, createEffect, createMemo, createSignal, For, Show } from "solid-js"
import { createMutable } from "solid-js/store"
import createEngineForUI from "../../lib/core/engine/createEngineForUI"
import { Engine } from "../../lib/core/engine/Engine.type"
import createSampleEntity from "../../lib/core/entity/createSampleEntity"
import { EntityTypeId } from "../../lib/core/entity/EntityTypeId.enum"
import { type ColorNamePair, colorNamesPool, makeColorNamePair } from "../../lib/core/player/colorNamesPool"
import createBlankPlayer from "../../lib/core/player/createBlankPlayer"
import type { Player } from "../../lib/core/player/Player.type"
import type { PlayerId } from "../../lib/core/player/PlayerId"
import PlayerColorsBar from "../ArmyPainter/PlayerColorsBar"
import EngineContext from "../EngineContext"
import SingleEntityIcon3D from "./SingleEntityIcon3D"

interface Props {
  // value: number | null
  player: Player
  onChange: (colorPair: ColorNamePair) => void
}

const SelectPlayerColorFromPoolDropDownButton: Component<Props> = (props) => {

  const engine: Engine = createMutable(createEngineForUI())

  const [isDropDownOpen, setIsDropDownOpen] = createSignal<boolean>(false)

  const currentColorPair = createMemo<ColorNamePair>(() => {
    const { color1, color2 } = props.player
    return makeColorNamePair(color1, color2, 'Custom')
  })

  const currentColorPairPlayer = createMemo<Player>(() => {
    const id: PlayerId = 1
    const player = createBlankPlayer(id, currentColorPair())
    return player
  })

  const selectedColor = createMemo<ColorNamePair | undefined>(() => {
    const { color1, color2 } = props.player
    return colorNamesPool.find((c) => c.color1 === color1 && c.color2 === color2)
      || currentColorPair()
  })

  const dummyPlayers = createMemo<Array<Player>>(() => {
    return colorNamesPool.map((color: ColorNamePair, index): Player => {
      const id: PlayerId = 100 + index
      const player = createBlankPlayer(id, color)
      return player
    })
  })

  createEffect(() => {
    engine.state.players = [
      ...dummyPlayers(),
      currentColorPairPlayer(),
    ]
  })

  return <EngineContext.Provider value={engine}>
    <div class="dropdown">
      <button
        class="btn btn-secondary dropdown-toggle w-100"
        type="button"
        aria-expanded={isDropDownOpen()}
        onClick={() => setIsDropDownOpen(!isDropDownOpen())}
      >
        <SingleEntityIcon3D
          entity={createSampleEntity(EntityTypeId.LightTank, currentColorPairPlayer().id)}
          size={24}
        />
        &nbsp;<PlayerColorsBar value={currentColorPairPlayer()} width={16} height={16} />
        &nbsp;{selectedColor()?.dname ?? 'Unknown'}
      </button>
      <ul classList={{ 'dropdown-menu': true, 'show': isDropDownOpen(), 'w-100': true }}>
        <For each={colorNamesPool} children={(color: ColorNamePair) => {
          const player = createMemo<Player | undefined>(() => {
            return engine.state.players.find((p) => p.color1 === color.color1 && p.color2 === color.color2)
          })
          return (
            <li>
              <button
                class="dropdown-item"
                type="button"
                onClick={() => {
                  props.onChange(color)
                  setIsDropDownOpen(false)
                }}
              >
                <Show when={player()?.id} children={(playerId) => {
                  return <SingleEntityIcon3D
                    entity={createSampleEntity(EntityTypeId.LightTank, playerId())}
                    size={32}
                  />
                }} />
                &nbsp;
                <PlayerColorsBar value={color} width={16} height={16} />
                &nbsp;{color.dname}
              </button>
            </li>
          )
        }} />
      </ul>
    </div>
  </EngineContext.Provider>
}

export default SelectPlayerColorFromPoolDropDownButton
